import { Button, Checkbox, Collapse, Form, Input, message, Spin, Typography } from "antd";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import useSWR from "swr";
import {
  CreateSurveyCampaignRequest,
  GetSurveyCampaignBySurveyCampaignIdResponse,
  GetSurveyTemplatesBySurveyTemplateIdResponse,
  UpdateSurveyCampaignBasicRequest,
} from "../../constants/apiRequestResponse";
import TEST_IDS from "../../constants/cypressTestIds";
import {
  createSurveyCampaign,
  fetcher,
  updateSurveyCampaignBasic,
} from "../../services/api";
import { SurveyCampaignFormKeys } from "../../services/i8tn/SurveyCampaignForm/keys";
import { getQueryParam, parseDate } from "../../util";

export default function BasicInfo() {
  const isEditMode = window.location.pathname.includes("edit");

  const { t } = useTranslation();
  const history = useHistory();
  const [isNextButtonLoading, setIsNextButtonLoading] = useState(false);
  const [form] = Form.useForm();

  const surveyTemplateId = getQueryParam("surveyTemplateId");

  const { data: getSurveyCampaignRes, isLoading: isSurveyCampaignLoading } =
    useSWR<GetSurveyCampaignBySurveyCampaignIdResponse>(
      isEditMode
        ? `/surveyCampaign/getBySurveyCampaignId/${getQueryParam(
          "surveyCampaignId"
        )}`
        : null,
      fetcher,
      {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        errorRetryCount: 0,
      }
    );

  const { data: getSurveyTemplateRes, isLoading: isSurveyTemplateLoading } =
    useSWR<GetSurveyTemplatesBySurveyTemplateIdResponse>(
      !isEditMode && surveyTemplateId
        ? `/surveyTemplate/${surveyTemplateId}`
        : null,
      fetcher,
      {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        errorRetryCount: 0,
      }
    );

  // Use categories from survey template (create mode) or survey campaign (edit mode)
  const availableCategories = isEditMode
    ? getSurveyCampaignRes?.categories || []
    : getSurveyTemplateRes?.categories || [];

  if (getSurveyCampaignRes?.surveyCampaign) {
    form.setFieldsValue(getSurveyCampaignRes?.surveyCampaign);
  }

  // Set selected categories based on checked field from API
  if (availableCategories.length > 0) {
    const checkedCategories = availableCategories
      .filter((cat) => cat.checked)
      .map((cat) => cat.name);
    form.setFieldsValue({ selectedCategories: checkedCategories });
  }

  return (
    <div>
      <div>
        <Typography.Title level={3}>
          {t(SurveyCampaignFormKeys.surveyCampaignForm_basicInfoString)}
        </Typography.Title>
      </div>
      <Form
        form={form}
        layout="vertical"
        onFinish={async () => {
          setIsNextButtonLoading(true);

          try {
            const formValues = form.getFieldsValue(true) as {
              name: string,
              selectedCategories: string[]
            };
            if (isEditMode) {
              const surveyCampaignId = getQueryParam("surveyCampaignId") || "";
              const req: UpdateSurveyCampaignBasicRequest = {
                name: formValues.name,
                selectedCategories: formValues.selectedCategories,
              };
              await updateSurveyCampaignBasic(req, surveyCampaignId);

              history.push(
                `/surveycampaign/edit/employees?surveyCampaignId=${surveyCampaignId}`
              );
            } else {
              const req: CreateSurveyCampaignRequest = {
                surveyCampaign: {
                  name: formValues.name,
                  surveyTemplateId: getQueryParam("surveyTemplateId") || "",
                  selectedCategories: formValues.selectedCategories,
                },
              };
              const res = await createSurveyCampaign(req);
              history.push(
                `/surveycampaign/edit/employees?surveyCampaignId=${res.surveyCampaignId}`
              );
            }
          } catch (err) {
            switch (typeof err) {
              case "object":
                message.error(err?.toString());
                break;
              default:
                message.error(err);
            }
            setIsNextButtonLoading(false);
          }
        }}
      >
        <Form.Item
          data-testid={TEST_IDS.SURVEY_CAMPAIGN_FORM.campaign_name_label_id}
          name="name"
          rules={[
            {
              message: "Please enter a name for the survey campaign",
              required: true,
            },
          ]}
        >
          {isSurveyCampaignLoading ? (
            <Spin />
          ) : (
            <Input placeholder={`e.g. My Survey ${parseDate(moment.now())}`} />
          )}
        </Form.Item>

        <Collapse>
          <Collapse.Panel header={t(SurveyCampaignFormKeys.surveyCampaignForm_advancedString)} key="advanced">
            <Form.Item
              name="selectedCategories"
              label={t(SurveyCampaignFormKeys.surveyCampaignForm_categoriesToIncludeLabel)}
            >
              {(isEditMode ? isSurveyCampaignLoading : isSurveyTemplateLoading) ? (
                <Spin />
              ) : (
                <Checkbox.Group
                  options={availableCategories.map((cat) => ({
                    label: `${cat.name} (${cat.count})`,
                    value: cat.name,
                  }))}
                />
              )}
            </Form.Item>
          </Collapse.Panel>
        </Collapse>

        <Form.Item>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 30,
            }}
          >
            <Button
              htmlType="submit"
              type="primary"
              loading={isNextButtonLoading}
            >
              {isEditMode ? (
                <>{t(SurveyCampaignFormKeys.surveyCampaignForm_nextBtn)}</>
              ) : (
                <>{t(SurveyCampaignFormKeys.surveyCampaignForm_createBtn)}</>
              )}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
}
